import { faCaretUp, faChartBar, faClipboard, faMagnifyingGlass } from '@fortawesome/pro-duotone-svg-icons';
import { faCalendarRange, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Checkbox, Chip, CircularProgress, Collapse, Drawer, FormControlLabel, FormGroup, Grid, IconButton, MenuItem, Radio, RadioGroup, SelectChangeEvent, TextField, Tooltip, Typography, styled, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { appendQueryString } from '@servicestack/client';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEventHandler, Dispatch, FC, ReactNode, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BySubmitter, Species } from '../../api/dtos/dtos';
import AuthContext from '../../contexts/auth/AuthContext';
import MapContext, { MapFilterPeriodEnum, MapFilterSubmitter } from '../../contexts/data/MapContext';
import { SpeciesContext } from '../../contexts/data/SpeciesContext';
import UserGroupsContext from '../../contexts/data/UserGroupsContext';
import { LayerType } from '../../enums/LayerType';
import isCurrentGroupAdmin from '../../functions/user';
import useGetCommunityGroupMembers from '../../hooks/data/useGetCommunityGroupMembers';
import Center from '../common/Center';
import CustomSelect from '../common/Select';
import { IHasChildren } from '../common/types/IHasChildren';
import { MapFilterUrlParams } from '../providers/styles/MapProvider';


const SpacingBox = styled(Box)(({ theme }) => ({
    ...theme.mixins.toolbar,
    height: 75
}));

const StyledFilterContainer = styled(Box)(() => ({
    backgroundColor: '#f1f1f1',
    borderRadius: '10px',
    alignSelf: 'center',
    padding: '10px',
    width: '100%',
    height: '100%'
}));

const StyledConfirmButton = styled(Button)(({ theme }) => ({
    maxWidth: '100%',
    minWidth: '100%',
    minHeight: '40px',
    maxHeight: '40px',
    backgroundColor: theme.palette.primary.main,
    color: grey[100],

    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: theme.palette.secondary.main
    },

    "&:disabled": {
        backgroundColor: grey[300],
        color: grey[600]
    }
}));

const StyledClipboardButton = styled(Button)(({ theme }) => ({
    maxWidth: '30px',
    maxHeight: '40px',
    backgroundColor: 'transparent',
    border: `3px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,

    "&:hover": {
        backgroundColor: 'transparent',
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:active": {
        backgroundColor: `${theme.palette.secondary.main}01`
    }
}));

const StyledDatePicker = styled(DatePicker)(() => ({
    marginBottom: '15px',
    "&  input": {
        padding: '5px 15px',
    }
}));

const StyledGridContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gridGap: '15px',
}));

const StyledGrid = styled(Box)(() => ({
    flex: '1'
}));

interface IStyledTextFieldProps extends IHasChildren {
    disabled?: boolean;
    loading?: boolean;
    value?: any;
    onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    icon?: ReactNode;
}

const StyledSelect: FC<IStyledTextFieldProps> = ({
    disabled,
    loading,
    value,
    onChange,
    icon,
    children
}) => {
    const theme = useTheme();

    const [open, setOpen] = useState<boolean>(false);

    return (
        <TextField
            select
            disabled={!!disabled}
            fullWidth
            size='small'
            value={value}
            onChange={onChange}
            sx={{
                '& label': {
                    color: grey[700]
                },
                '& p': {
                    color: grey[700]
                },
                '& .MuiOutlinedInput-root': {
                    paddingRight: '0px',
                    '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.2)',
                    },
                    '&:hover fieldset': {
                        borderColor: disabled ?
                            'rgba(0, 0, 0, 0.2)' :
                            'rgb(0, 0, 0)',
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main
                    },
                },
            }}
            SelectProps={{
                sx: { "& .MuiSelect-icon": { top: 10 } },
                onOpen: () => setOpen(true),
                onClose: () => setOpen(false),
                IconComponent: () => undefined
            }}
            InputProps={{
                endAdornment: icon ? (
                    <Center width='0px'>
                        {
                            loading &&
                            <CircularProgress sx={{ position: 'absolute', right: '12px', color: grey[500], maxWidth: '16px', maxHeight: '16px' }} />
                        }
                        <FontAwesomeIcon
                            style={{
                                position: 'absolute',
                                right: '15px',
                                rotate: open ? '0deg' : '180deg'
                            }}
                            color={grey[600]}
                            icon={faCaretUp}
                        />
                    </Center>
                ) : undefined
            }}
            InputLabelProps={{
                shrink: true
            }}
        >
            {children}
        </TextField>
    )
}


export const DEFAULT_GROUP_MEMBER_ID = -1
export const DEFAULT_KML_ID = -1

const MapFilterDrawer: FC = () => {
    let navigate = useNavigate();

    //
    // -- Contexts --
    const { data: userGroupsConfig, loading: userGroupsConfigLoading } = useContext(UserGroupsContext);
    const { species: { species, subSpecies } } = useContext(SpeciesContext);
    const { user } = useContext(AuthContext);
    const {
        drawerOpen,
        filter,
        setPeriodFilter,
        setSubmitterFilter,
        setRecordTypeFilter,
        setLayersFilter,
        closeDrawer
    } = useContext(MapContext);

    //
    // -- Params -- 
    const { species: speciesName } = useParams();
    const location = useLocation();

    //
    // -- States --
    const [disabled, setDisabled] = useState<boolean>(false);
    const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
    const timeoutRef = useRef<number | null>(null);

    //
    // -- Queries --
    const { data: groupMembers, loading: groupMembersLoading } = useGetCommunityGroupMembers(
        (filter.bySubmitter.communityGroupId && filter.bySubmitter.communityGroupId != -1) ?
            { communityGroupId: filter.bySubmitter.communityGroupId } :
            undefined,
        { errorRetryCount: 0 }
    );

    //
    // -- Cached --
    //
    // User fields
    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    const isDataAccess = useMemo(() => {
        return !!user?.roles?.find(x => x === 'dataaccess');
    }, [user]);

    const isDataAccessLimited = useMemo(() => {
        return !!user?.roles?.find(x => x === 'dataaccess_limited');
    }, [user]);

    const isGroupAdmin = useMemo(() => {
        return isCurrentGroupAdmin(userGroupsConfig, user, filter);
    }, [userGroupsConfig, user, filter, isCurrentGroupAdmin]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return user?.matchingRegionAdminRoles != undefined && user?.matchingRegionAdminRoles?.length > 0;
    }, [user]);

    // Record types
    const showDamageRecordType = useMemo(() => {
        return species !== Species.NewPests;
    }, [species]);

    const showControlRecordType = useMemo(() => {
        return !!user;
    }, [user]);

    const showDiseaseRecordType = useMemo(() => {
        return species === Species.Rabbit;
    }, [user, species]);

    // Submitter options
    const showAllData = useMemo(() => {
        return !!user;
    }, [user]);
    const showMyData = useMemo(() => {
        return !!user;
    }, [user]);
    const showMyGroup = useMemo(() => {
        return !!user;
    }, [user]);

    // Community groups
    const communityGroups = useMemo(() => {
        const groups = userGroupsConfig?.communityGroups ?? [];

        const userId = user?.id;

        return groups
            .filter(g => {
                const admin = g.adminUserId == userId;
                const members = g.communityGroupMembers;
                const speciesList = g.communityGroupSpecies;

                const hasAccess = members?.some(
                    m => m.userId === userId && m.viewRights
                ) || admin || isAdminOrSuperAdmin;

                if (!hasAccess) return false;

                if (!speciesList || speciesList.length === 0) return true;

                return speciesList.some(
                    cgs =>
                        (cgs.speciesId === species && cgs.linkGroupToSpecies === 1) ||
                        cgs.linkGroupToSpecies === 0
                );
            })
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }, [isAdminOrSuperAdmin, userGroupsConfig?.communityGroups, user?.id, species]);

    const members = useMemo(() => {
        return groupMembers ?? [];
    }, [groupMembers]);

    //
    // -- Callbacks --
    //
    // OnClick event of the "Copy to clipboard" button.
    // Needs to generate the link and also set a timeout for tooltip...
    const handleCopyToClipboardClick = useCallback(() => {
        const r = appendQueryString(MapFilterUrlParams.getURLParams({
            reportingPeriod: filter.reportingPeriod,
            bySubmitter: filter.bySubmitter,
            recordType: filter.recordType,
            layers: filter.layers
        }), undefined);

        navigator.clipboard.writeText(window.location.protocol + "//" + window.location.host + window.location.pathname + r);

        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
        }

        setTooltipOpen(true);

        timeoutRef.current = window.setTimeout(() => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = null;
            setTooltipOpen(false);
        }, 8000);

    }, [window.location.href, filter, timeoutRef, setTooltipOpen]);

    // Handles the closure of the filter drawer
    const handleCloseDrawer = useCallback(() => {
        if (timeoutRef.current !== null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
            setTooltipOpen(false);
        }
        closeDrawer()
    }, [timeoutRef, closeDrawer]);

    // Handles the change of any radio fields in the filter
    const handleRadioChange = useCallback(<T extends number, U extends Record<string, any>>(
        value: string,
        fallback: T,
        theEnum: any,
        field: string,
        label: string | undefined,
        dispatcher: Dispatch<SetStateAction<U>>
    ) => {
        let newValue = Number(value);

        if (!Number.isInteger(newValue) || !Object.values(theEnum).includes(newValue)) {
        newValue = fallback;
        }

        // Non-submitter fields are simple
        if (field !== 'submitter') {
            dispatcher(c => ({
                ...c,
                [field]: newValue,
            }));
            return;
        }

        let resolvedLabel = label;

        if (label === 'My Group') {
            const { communityGroupMemberId, communityGroupId } = filter.bySubmitter;

            resolvedLabel =
                communityGroupMemberId !== -1
                ? members.find(m => m.userId === communityGroupMemberId)?.user?.fullName
                : communityGroupId !== -1
                    ? communityGroups?.find(g => g.communityGroupId === communityGroupId)?.name
                    : communityGroups?.[0]?.name;
        }

        dispatcher(c => ({
            ...c,
            [field]: newValue,
            submitterLabel: resolvedLabel ?? c.submitterLabel,
            communityGroupId: newValue === 4 ? filter.bySubmitter.communityGroupId : -1,
            communityGroupMemberId: newValue === 4 ? filter.bySubmitter.communityGroupMemberId : -1,
        }));
    },
    [filter.bySubmitter, members, communityGroups]);

    // Handles the change of a period filter
    const handleDateChange = useCallback((value: Dayjs | null, field: string) => {
        setPeriodFilter(c => ({
            ...c,
            [field]: value
        }));
    }, [setPeriodFilter]);

    // Handles the change of a record type checkbox
    const handleRecordTypeChange = useCallback((value: boolean, field: string) => {
        setRecordTypeFilter(c => ({
            ...c,
            [field]: value
        }));
    }, [setRecordTypeFilter]);

    const handleCheckBoxChange = useCallback((value: boolean, type: LayerType) => {
        if (type === LayerType.BARRIER_FENCES) {
            setLayersFilter(c => ({
                ...c,
                barrierFences: value
            }));
        }
        else if (type === LayerType.KNOWN_REGIONS) {
            setLayersFilter(c => ({
                ...c,
                knownRegions: value
            }));
        }
        else if (type === LayerType.RELEASE_SITES) {
            setLayersFilter(c => ({
                ...c,
                releaseSites: value
            }));
        }
        else if (type === LayerType.RHD_BOOST_ROLLOUT_SITES) {
            setLayersFilter(c => ({
                ...c,
                rhdBoostRollOutSites: value
            }));
        }
        else if (type === LayerType.SUCCESSFUL_EOI_SITES) {
            setLayersFilter(c => ({
                ...c,
                successfulEoiSites: value
            }));
        }
    }, [setLayersFilter]);

    const handleSelectValueChange = useCallback(
        <T extends number | undefined, U>(
            value: string,
            fallback: T,
            field: string,
            label: string | undefined,
            dispatcher: Dispatch<SetStateAction<U>>
        ) => {
            let newValue = parseInt(value);

            if (isNaN(newValue)) {
                newValue = fallback as number;
            }

            if (label === undefined) {
                const { communityGroupId } = filter.bySubmitter;

                if (field === 'communityGroupMemberId') {
                    label =
                        newValue !== -1
                            ? members.find(m => m.userId === newValue)?.user?.fullName
                            : communityGroups?.find(g => g.communityGroupId === communityGroupId)?.name;
                }

                if (field === 'communityGroupId') {
                    label =
                        newValue !== -1
                            ? communityGroups?.find(g => g.communityGroupId === newValue)?.name
                            : communityGroups?.[0]?.name;
                }
            }

            dispatcher((c: U) => ({
                ...c,
                [field]: newValue,
                submitterLabel: label ?? (c as any).submitterLabel
            }));
        },
        [members, communityGroups, filter]
    );

    const handleKmlSelection = useCallback((value: number[]) => {
        setLayersFilter(c => ({
            ...c,
            selectedCommunityGroupKmls: value.length < 6 ? value : value.slice(0, 6)
        }))
    }, [setLayersFilter]);

    const handleCustomHomPageRegionSelection = useCallback((value: string[]) => {
        if (value.length < 1) {
            return;
        }

        setLayersFilter(c => ({
            ...c,
            selectedCustomHomePageRegions: value
        }))
    }, [setLayersFilter]);

    const handleSubSpeciesSelection = useCallback((value: number) => {
        setRecordTypeFilter(c => ({
            ...c,
            subSpeciesId: value
        }))
    }, [setRecordTypeFilter]);

    // Set the global state for the map filter and close drawer
    const handleViewMapClick = useCallback(() => {
        setDisabled(true);

        if (filter.reportingPeriod.periodLabel != undefined) {
            setPeriodFilter(c => ({
                ...c,
                periodLabel: undefined
            }));
        }

        if (!location.pathname.includes("map") || location.search.length > 0) {
            navigate(`/${speciesName}/map`)
        }

        setTimeout(() => {
            closeDrawer();
            setDisabled(false);
        }, 50);
    }, [
        setDisabled,
        location,
        filter
    ]);

    // Set the global state for the map filter and navigate to the given path
    const handleViewDataClick = useCallback(() => {
        setDisabled(true);

        if (!location.pathname.includes("datamanagement")) {
            navigate(`/${speciesName}/datamanagement`);
        }

        setTimeout(() => {
            closeDrawer();
            setDisabled(false);
        }, 50);
    }, [
        setDisabled,
        location
    ]);

    // Set the global state for the map filter and navigate to the given path
    const handleViewChartsClick = useCallback(() => {
        setDisabled(true);

        if (!location.pathname.includes("chartsandtables")) {
            navigate(`/${speciesName}/chartsandtables`);
        }

        setTimeout(() => {
            closeDrawer();
            setDisabled(false);
        }, 50);
    }, [
        setDisabled,
        location
    ]);

    //
    // -- useEffect --
    //
    // Cleanup for tooltip style
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current)
            }
        }
    }, [timeoutRef]);

    function arraysEqual(a?: number[], b?: number[]) {
        if (a === b) return true;
        if (!a || !b || a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }

    useEffect(() => {
        if (isAdminOrSuperAdmin) {
            return;
        }
        
        if (!isDataAccessForThisSpecies) {
            return;
        }

        if (!user?.matchingRegionAdminRoles?.length) {
            return;
        }

        const regionNameList = user.matchingRegionAdminRoles
            .filter(r => r.region)
            .map(r => {
                if (r.region?.name?.trim()) {
                    return r.region.name;
                }
                if (r.region?.state?.trim()) {
                    return r.region.state;
                }
                return "";
            })
            .filter(Boolean);

        const regionIdList = user.matchingRegionAdminRoles
            .map(r => r.regionId)
            .filter((id): id is number => id !== undefined);

        setSubmitterFilter(c => {
            const regionNameStr = regionNameList.join(", ");
            const hasChanged =
                c.dataAccessId !== user.id ||
                !arraysEqual(c.locatedInRegionIds, regionIdList) ||
                c.submitterLabel !== regionNameStr;

            if (!hasChanged) return c; // don't update if identical

            return {
                ...c,
                locatedInRegionIds: regionIdList,
                dataAccessId: user.id,
                submitterLabel: regionNameStr
            };
        });
    }, [
        isDataAccessForThisSpecies,
        user?.id,
        user?.matchingRegionAdminRoles,
        setSubmitterFilter,
        filter
    ]);


    // Update the selected community group in the filter upon init
    useEffect(() => {
        if (!groupMembersLoading) {
            if (!filter.bySubmitter.communityGroupId && (communityGroups.length) > 0) {
                setSubmitterFilter(c => ({
                    ...c,
                    communityGroupId: communityGroups[0].communityGroupId,
                    communityGroupMemberId: DEFAULT_GROUP_MEMBER_ID
                }));
            } else if (filter.bySubmitter.communityGroupId && communityGroups?.length > 0 && 
                !communityGroups.find(x => x.communityGroupId === filter.bySubmitter.communityGroupId)) {
                setSubmitterFilter(c => ({
                    ...c,
                    communityGroupId: undefined,
                    communityGroupMemberId: DEFAULT_GROUP_MEMBER_ID
                }));
            }
            if (communityGroups?.length > 0 && filter.bySubmitter.communityGroupId && 
                filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup && filter.layers.communityGroupKmls.length == 0) {
                setLayersFilter(c => ({
                    ...c,
                    communityGroupKmls: communityGroups.find(cg => cg.communityGroupId === filter.bySubmitter.communityGroupId)?.additionalKmls ?? []
                }));
            }
            else if (filter.bySubmitter.submitter != BySubmitter.MyGroup) {
                setLayersFilter(c => ({
                    ...c,
                    communityGroupKmls: [],
                    selectedCommunityGroupKmls: []
                }));
            }
        }
    }, [filter.bySubmitter.communityGroupId, filter.bySubmitter.submitter, communityGroups, setLayersFilter, setSubmitterFilter, groupMembersLoading]);

    // TODO: Need to determine how this field is shown. I believe it is when a user has a group 
    //       or is a group admin?
    const displayLayers = useMemo(() => {
        if (filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup && filter.layers.communityGroupKmls.length > 0) {
            return true;
        }
        if (species === Species.WildDog || species === Species.Deer || species === Species.Rabbit ||
            species === Species.Fox || species === Species.FeralPig || species === Species.Myna ||
            species === Species.Toad || species === Species.FeralGoat || species === Species.Starling ||
            species === Species.FeralFish
        ) {
            return true;
        }
        if (filter.layers?.customHomePageRegions && filter.layers.customHomePageRegions?.length > 0) {
            return true;
        }
        return false;
    }, [filter.bySubmitter, filter.layers]);

    return (
        <Drawer
            anchor="top"
            open={drawerOpen}
            onClose={closeDrawer}
        >
            <SpacingBox />
            <Box
                sx={{
                    minHeight: '40px',
                    position: 'relative',
                    width: '100%',
                    marginTop: '15px'
                }}
            >
                <Box position='relative' width='100%' mt='2px'>
                    <Typography
                        textAlign='center'
                        fontSize='1.5rem'
                        fontWeight={600}
                        sx={{
                            color: '#333'
                        }}
                    >
                        Select Data
                    </Typography>
                </Box>
                <Center sx={{ position: 'absolute', right: '15px', top: '0px' }}>
                    <IconButton
                        sx={{
                            heigh: '40px',
                            width: '40px'
                        }}
                        onClick={handleCloseDrawer}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </IconButton>
                </Center>
                <Box sx={{ flexGrow: 1 }} margin='30px 30px 0 30px'>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={2}>

                            {/* -- Reporting Period -- */}
                            <Grid item xs={12} lg={displayLayers ? 3 : 4}>
                                <StyledFilterContainer>
                                    <Typography fontWeight={600}>Reporting period</Typography>
                                    <RadioGroup
                                        value={filter.reportingPeriod.period}
                                        onChange={(e) => handleRadioChange(e.target.value, MapFilterPeriodEnum.DateRange, MapFilterPeriodEnum, "period", undefined, setPeriodFilter)}
                                    >
                                        <FormControlLabel value={MapFilterPeriodEnum.DateRange} control={<Radio />} label="Date Range" />
                                        {
                                            filter.reportingPeriod.period === MapFilterPeriodEnum.DateRange &&
                                            <>
                                                <Typography mb='10px'>From:</Typography>
                                                <StyledDatePicker
                                                    minDate={dayjs(new Date('01-01-2008'))}
                                                    value={filter.reportingPeriod.fromField}
                                                    format='DD/MM/YYYY'
                                                    onChange={(e) => handleDateChange(e, 'fromField')}
                                                />
                                                <Typography mb='10px'>To:</Typography>
                                                <StyledDatePicker
                                                    maxDate={dayjs()}
                                                    format='DD/MM/YYYY'
                                                    value={filter.reportingPeriod.toField}
                                                    onChange={(e) => handleDateChange(e, 'toField')}
                                                />
                                            </>
                                        }
                                        <FormControlLabel value={MapFilterPeriodEnum.Year} control={<Radio />} label="Year" />
                                        {
                                            filter.reportingPeriod.period === MapFilterPeriodEnum.Year &&
                                            <StyledDatePicker
                                                value={filter.reportingPeriod.yearField}
                                                views={['year']}
                                                maxDate={dayjs()}
                                                onChange={(e) => handleDateChange(e, 'yearField')}
                                            />
                                        }
                                        <FormControlLabel value={MapFilterPeriodEnum.MonthYear} control={<Radio />} label="Month/Year" />
                                        {
                                            filter.reportingPeriod.period === MapFilterPeriodEnum.MonthYear &&
                                            <StyledDatePicker
                                                value={filter.reportingPeriod.monthYearField}
                                                views={['month', 'year']}
                                                maxDate={dayjs()}
                                                onChange={(e) => handleDateChange(e, 'monthYearField')}
                                            />
                                        }
                                        <FormControlLabel value={MapFilterPeriodEnum.LastSevenDays} control={<Radio />} label="Last 7 Days" />
                                        <FormControlLabel value={MapFilterPeriodEnum.LastThirtyOneDays} control={<Radio />} label="Last 31 Days" />
                                    </RadioGroup>
                                </StyledFilterContainer>
                            </Grid>

                            {/* -- By Submitter -- */}
                            <Grid item xs={12} lg={displayLayers ? 3 : 4}>
                                <StyledFilterContainer>
                                    <Typography fontWeight={600}>By submitter</Typography>
                                    <RadioGroup
                                        value={filter.bySubmitter.submitter}
                                        onChange={(e) => handleRadioChange(e.target.value, MapFilterSubmitter.AllData, MapFilterSubmitter, "submitter", MapFilterSubmitter.toString(Number(e.target.value)), setSubmitterFilter)}
                                    >
                                        {
                                            (isAdminOrSuperAdmin ||
                                            (!isGroupAdmin &&
                                            !isDataAccessForThisSpecies)) && (
                                                <>
                                                    <FormControlLabel value={MapFilterSubmitter.PublicData} control={<Radio />} label="Public Data" />
                                                    <Collapse
                                                        in={filter.bySubmitter.submitter === MapFilterSubmitter.PublicData}
                                                    >
                                                        <FormControlLabel
                                                            sx={{ marginLeft: '15px' }}
                                                            control={
                                                                <Checkbox
                                                                    checked={filter.bySubmitter.displayHeatmap}
                                                                    onChange={(e) => setSubmitterFilter(c => ({
                                                                        ...c,
                                                                        displayHeatmap: e.target.checked
                                                                    }))}
                                                                />
                                                            }
                                                            label="Display Heatmap (all years)"
                                                        />
                                                    </Collapse>
                                                </>
                                            )}
                                        {
                                            showAllData &&
                                            (isAdminOrSuperAdmin || (isDataAccess || isDataAccessLimited)) &&
                                            !isGroupAdmin &&
                                            <FormControlLabel value={MapFilterSubmitter.AllData} control={<Radio />} label={isAdminOrSuperAdmin ? "All Data" : filter.bySubmitter.submitterLabel ? filter.bySubmitter.submitterLabel : "All Data"} />
                                        }
                                        {
                                            showMyData &&
                                            !isDataAccess &&
                                            !isDataAccessLimited &&
                                            !isGroupAdmin &&
                                            <FormControlLabel value={MapFilterSubmitter.MyData} control={<Radio />} label="My Data" />
                                        }
                                        {
                                            showMyGroup &&
                                            communityGroups.length > 0 &&
                                            <>
                                                <FormControlLabel value={MapFilterSubmitter.MyGroup} control={<Radio />} label="My Group" />
                                                <Collapse
                                                    in={filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup}
                                                >
                                                    <Box mx='15px' mt='10px'>
                                                        <Typography variant='body2' mb='5px'>Community Group</Typography>
                                                        <StyledSelect
                                                            disabled={!!disabled || userGroupsConfigLoading}
                                                            loading={userGroupsConfigLoading}
                                                            value={filter.bySubmitter.communityGroupId?.toString() ?? ''}
                                                            onChange={(e) => handleSelectValueChange(e.target.value, undefined, 'communityGroupId', communityGroups.find(x => x.communityGroupId?.toString() == e.target.value)?.name, setSubmitterFilter)}
                                                            icon={
                                                                <FontAwesomeIcon color={grey[600]} icon={faCaretUp} />
                                                            }
                                                        >
                                                            {
                                                                communityGroups
                                                                    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                                                                    .map(x => (
                                                                        <MenuItem key={`group-${x.communityGroupId}`} value={x.communityGroupId}>{x.name}</MenuItem>
                                                                    ))
                                                            }
                                                        </StyledSelect>

                                                        <Typography variant='body2' mb='5px' mt='15px'>Members</Typography>
                                                        <StyledSelect
                                                            disabled={!!disabled || groupMembersLoading}
                                                            loading={groupMembersLoading}
                                                            value={filter.bySubmitter.communityGroupMemberId?.toString() ?? ''}
                                                            onChange={(e) => handleSelectValueChange(e.target.value, undefined, 'communityGroupMemberId', members.find(x => x.userId?.toString() == e.target.value)?.user?.fullName, setSubmitterFilter)}
                                                            icon={
                                                                <FontAwesomeIcon color={grey[600]} icon={faCaretUp} />
                                                            }
                                                        >
                                                            <MenuItem key='group-member-all' value={DEFAULT_GROUP_MEMBER_ID}>All members</MenuItem>
                                                            {
                                                                members
                                                                    .sort((a, b) => (a.user?.fullName ?? '').localeCompare(b.user?.fullName ?? ''))
                                                                    .map(x => (
                                                                        <MenuItem key={`group-member-${x.communityGroupMemberId}`} value={x.userId}>{x.user?.fullName ?? ''}</MenuItem>
                                                                    ))
                                                            }
                                                        </StyledSelect>
                                                    </Box>
                                                </Collapse>
                                            </>
                                        }
                                    </RadioGroup>
                                </StyledFilterContainer>
                            </Grid>

                            {/* -- Record Type -- */}
                            <Grid item xs={12} lg={displayLayers ? 3 : 4}>
                                <StyledFilterContainer>
                                    <Typography fontWeight={600}>Record type</Typography>
                                    <FormGroup>
                                        <FormControlLabel control={<Checkbox checked={filter.recordType.sighting} onChange={(e) => handleRecordTypeChange(e.target.checked, "sighting")} />} label="Sighting" />
                                        {
                                            showDamageRecordType &&
                                            <FormControlLabel control={<Checkbox checked={filter.recordType.damage} onChange={(e) => handleRecordTypeChange(e.target.checked, "damage")} />} label="Damage" />
                                        }
                                        {
                                            showControlRecordType &&
                                            <FormControlLabel control={<Checkbox checked={filter.recordType.control ?? false} onChange={(e) => handleRecordTypeChange(e.target.checked, "control")} />} label="Control" />
                                        }
                                        {
                                            showDiseaseRecordType &&
                                            <FormControlLabel control={<Checkbox checked={filter.recordType.disease ?? false} onChange={(e) => handleRecordTypeChange(e.target.checked, "disease")} />} label="Disease" />
                                        }

                                    </FormGroup>
                                    {/* SubSpecies Selector */}
                                    {
                                        subSpecies && subSpecies.length > 0 && 
                                        <Box mt='10px'>
                                            <Typography variant='body2' mb='5px'>Species</Typography>
                                            <CustomSelect
                                                disabled={!!disabled}
                                                fullWidth
                                                size='small'
                                                SelectProps={{
                                                    renderValue: (selected: any) => (
                                                        <Typography>{subSpecies!.find(x => x.id === selected)?.value}</Typography>
                                                    ),
                                                    value: filter.recordType?.subSpeciesId,
                                                    onChange: (e: SelectChangeEvent<unknown>) => handleSubSpeciesSelection(e.target.value as number)
                                                }}
                                            >
                                                {
                                                    subSpecies.map(x => (
                                                        <MenuItem key={`subspecies-${x.id}-${x.value}`} value={x.id}>{x.value}</MenuItem>
                                                    ))
                                                }
                                            </CustomSelect>
                                        </Box>
                                    }
                                </StyledFilterContainer>
                            </Grid>

                            {/* -- Layers -- */}
                            {
                                displayLayers &&
                                <Grid item xs={12} lg={3}>
                                    <StyledFilterContainer>
                                        <Typography fontWeight={600}>Layers</Typography>
                                        {/* Barrier fences checkbox */}
                                        {
                                            (species === Species.WildDog || species === Species.Rabbit || species === Species.FeralFish) &&
                                            <Box sx={{ paddingTop: '10px' }}>
                                                <FormControlLabel
                                                    label={species === Species.FeralFish ? "Upper Murrumbidgee Catchment" : "Barrier fences"}
                                                    control={
                                                        <Checkbox
                                                            checked={filter.layers.barrierFences}
                                                            onChange={(e) => handleCheckBoxChange(e.target.checked, LayerType.BARRIER_FENCES)}
                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                        />
                                                    }
                                                />
                                            </Box>
                                        }
                                        {/* Known species region checkbox */}
                                        {
                                            (
                                                species === Species.WildDog || species === Species.Deer || species === Species.Rabbit ||
                                                species === Species.Fox || species === Species.FeralPig || species === Species.Myna ||
                                                species === Species.Toad || species === Species.FeralGoat || species === Species.Starling ||
                                                species === Species.FeralFish
                                            ) &&
                                            <Box>
                                                <FormControlLabel
                                                    label={species === Species.FeralFish ? "ACT" : `Known region for ${speciesName?.replace("scan", "").replace("alert", "")}`}
                                                    control={
                                                        <Checkbox
                                                            checked={filter.layers.knownRegions}
                                                            onChange={(e) => handleCheckBoxChange(e.target.checked, LayerType.KNOWN_REGIONS)}
                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                        />
                                                    }
                                                />
                                            </Box>
                                        }
                                        {/* Rabbit disease extras */}
                                        {
                                            (species === Species.Rabbit && filter.recordType.disease) && 
                                            <>
                                                <Box>
                                                    <FormControlLabel
                                                        label={'Release sites'}
                                                        control={
                                                            <Checkbox
                                                                checked={filter.layers.releaseSites}
                                                                onChange={(e) => handleCheckBoxChange(e.target.checked, LayerType.RELEASE_SITES)}
                                                                inputProps={{ 'aria-label': 'controlled' }}
                                                            />
                                                        }
                                                    />
                                                </Box>
                                                { 
                                                    isAdminOrSuperAdmin && (
                                                        <>
                                                            <Box>
                                                                <FormControlLabel
                                                                    label={'RHD boost roll-out sites'}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={filter.layers.rhdBoostRollOutSites}
                                                                            onChange={(e) => handleCheckBoxChange(e.target.checked, LayerType.RHD_BOOST_ROLLOUT_SITES)}
                                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                                        />
                                                                    }
                                                                />
                                                            </Box>
                                                            <Box>
                                                                <FormControlLabel
                                                                    label={'Successful EOI sites (Feb 2017)'}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={filter.layers.successfulEoiSites}
                                                                            onChange={(e) => handleCheckBoxChange(e.target.checked, LayerType.SUCCESSFUL_EOI_SITES)}
                                                                            inputProps={{ 'aria-label': 'controlled' }}
                                                                        />
                                                                    }
                                                                />
                                                            </Box>
                                                        </>
                                                    )
                                                }
                                            </>
                                        }
                                        {/* Additional community group regions */}
                                        {
                                            filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup && filter.layers.communityGroupKmls.length > 0 &&
                                            <Box mx='15px' mt='10px'>
                                                <Typography variant='body2' mb='5px'>Community Group Areas</Typography>
                                                <CustomSelect
                                                    disabled={!!disabled}
                                                    fullWidth
                                                    size='small'
                                                    SelectProps={{
                                                        multiple: true,
                                                        renderValue: (selected: any) => (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {selected.map((value: any) => (
                                                                    <Chip key={value} label={filter.layers.communityGroupKmls.find(x => x.id === value)?.name} />
                                                                ))}
                                                            </Box>
                                                        ),
                                                        value: filter.layers.selectedCommunityGroupKmls,
                                                        onChange: (e: SelectChangeEvent<unknown>) => handleKmlSelection(e.target.value as number[])
                                                    }}
                                                >
                                                    {
                                                        filter.layers.communityGroupKmls.map(x => (
                                                            <MenuItem key={`kml-${x.communityGroupId}-${x.id}`} value={x.id}>{x.name}</MenuItem>
                                                        ))
                                                    }
                                                </CustomSelect>
                                            </Box>
                                        }
                                        {/* Custom Home Page Regions */}
                                        {
                                            filter.layers?.customHomePageRegions && filter.layers.customHomePageRegions.length > 0 && 
                                            <Box mx='15px' mt='10px'>
                                                <Typography variant='body2' mb='5px'>Regions</Typography>
                                                <CustomSelect
                                                    disabled={!!disabled}
                                                    fullWidth
                                                    size='small'
                                                    SelectProps={{
                                                        multiple: true,
                                                        renderValue: (selected: any) => (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                {selected.map((value: any) => (
                                                                    <Chip key={value} label={filter.layers.customHomePageRegions!.find(x => x.title === value)?.title} />
                                                                ))}
                                                            </Box>
                                                        ),
                                                        value: filter.layers.selectedCustomHomePageRegions,
                                                        onChange: (e: SelectChangeEvent<unknown>) => handleCustomHomPageRegionSelection(e.target.value as string[])
                                                    }}
                                                >
                                                    {
                                                        filter.layers.customHomePageRegions.map(x => (
                                                            <MenuItem key={`custom-home-page-${x.title}`} value={x.title}>{x.title}</MenuItem>
                                                        ))
                                                    }
                                                </CustomSelect>
                                            </Box>
                                        }
                                    </StyledFilterContainer>
                                </Grid>
                            }
                        </Grid>
                    </LocalizationProvider>
                </Box>
                <Box
                    width='100%'
                    marginTop='15px'
                    padding='0 30px 15px 30px'
                    justifyContent='center'
                >
                    <StyledGridContainer>
                        {((user && filter.bySubmitter.submitter !== MapFilterSubmitter.PublicData) || isAdminOrSuperAdmin) && (
                            <>
                                {isAdminOrSuperAdmin}
                                <StyledGrid>
                                    <Center>
                                        <StyledConfirmButton
                                            disabled={disabled}
                                            onClick={handleViewDataClick}
                                        >
                                            <FontAwesomeIcon fontSize='1.5rem' icon={faCalendarRange} />
                                            <Typography marginLeft='15px' fontWeight='600'>
                                                {(filter.bySubmitter.submitter === MapFilterSubmitter.MyData || filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup) 
                                                    ? "View Data & Photos" 
                                                    : "View Data"
                                                }
                                            </Typography>
                                        </StyledConfirmButton>
                                    </Center>
                                </StyledGrid>
                                <StyledGrid>
                                    <Center>
                                        <StyledConfirmButton
                                            disabled={disabled}
                                            onClick={handleViewChartsClick}
                                        >
                                            <FontAwesomeIcon fontSize='1.5rem' icon={faChartBar} />
                                            <Typography marginLeft='15px' fontWeight='600'>View Charts</Typography>
                                        </StyledConfirmButton>
                                    </Center>
                                </StyledGrid>
                            </>
                        )}
                        <StyledGrid>
                            <Center>
                                <StyledConfirmButton
                                    disabled={disabled}
                                    onClick={handleViewMapClick}
                                >
                                    <FontAwesomeIcon fontSize='1.5rem' icon={faMagnifyingGlass} />
                                    <Typography marginLeft='15px' fontWeight='600'>View Map</Typography>
                                </StyledConfirmButton>
                            </Center>
                        </StyledGrid>
                        {user && (
                            <Box
                                onMouseOver={() => setTooltipOpen(true)}
                                onMouseLeave={() => setTooltipOpen(false)}
                            >
                                <Tooltip
                                    arrow
                                    placement='left'
                                    open={tooltipOpen}
                                    title={
                                        <Typography>Copy these filter settings to the clipboard</Typography>
                                    }
                                >
                                    <StyledClipboardButton
                                        fullWidth
                                        onClick={handleCopyToClipboardClick}
                                    >
                                        <FontAwesomeIcon fontSize='1.5rem' icon={faClipboard} />
                                    </StyledClipboardButton>
                                </Tooltip>
                            </Box>
                        )}
                    </StyledGridContainer>
                </Box>
            </Box>
        </Drawer>
    )
}

export default MapFilterDrawer